import * as yup from "yup";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { SpeakingClassLevel } from "../gql/graphql.ts";
import { Col } from "../UI/Layout/Col.tsx";
import { Row } from "../UI/Layout/Row.tsx";
import { TextInput } from "../UI/TextInput.tsx";
import { InputWithLabel } from "../UI/InputWithLabel.tsx";
import { Select } from "@normasteaching/norma-ui";
import { PrimaryButton } from "../UI/PrimaryButton.tsx";
import { useWithLoading } from "../UI/Loading/useWithLoading.ts";
import { getAllSpeakingClassLevelOptions } from "../Topic/getAllSpeakingClassLevelOptions.ts";
import { useAllUserGroups } from "../UserGroup/useAllUserGroups.ts";

export type ProgressCheckPdfFormInputs = {
  level: SpeakingClassLevel;
  pdfUrl: string;
  userGroupId: string;
};

type Props = {
  defaultValues?: Partial<ProgressCheckPdfFormInputs>;
  onSave: (data: ProgressCheckPdfFormInputs) => Promise<void>;
  disabledMap?: {
    [key in keyof ProgressCheckPdfFormInputs]?: boolean;
  };
};

const schema = yup
  .object({
    level: yup
      .mixed<SpeakingClassLevel>()
      .oneOf(Object.values(SpeakingClassLevel))
      .required("Specifica un livello"),
    pdfUrl: yup
      .string()
      .url("Specifica un URL valido")
      .required("Specifica un URL per il PDF"),
    userGroupId: yup.string().required("Seleziona un user group"),
  })
  .required();

export function ProgressCheckPdfForm({
  defaultValues,
  onSave,
  disabledMap,
}: Props) {
  const levelOptions = getAllSpeakingClassLevelOptions();
  const { userGroups } = useAllUserGroups();
  const userGroupOptions = (userGroups ?? []).map((ug) => ({
    id: ug.id,
    name: `${ug.name} (${ug.slug})`,
  }));

  const {
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<ProgressCheckPdfFormInputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      level: levelOptions[0]?.id,
      ...(defaultValues ?? {}),
    } as ProgressCheckPdfFormInputs,
  });

  const { withLoading, loading } = useWithLoading();
  const onSubmit: SubmitHandler<ProgressCheckPdfFormInputs> = async (data) => {
    await withLoading(async () => onSave(data));
  };

  return (
    <form>
      <Col>
        <Row>
          <InputWithLabel label={"Level"}>
            <Controller
              control={control}
              render={({ field }) => (
                <Select<SpeakingClassLevel>
                  disabled={disabledMap?.level}
                  errorMessage={errors.level?.message as string}
                  onChange={field.onChange}
                  value={field.value}
                  options={levelOptions}
                />
              )}
              name={"level"}
            />
          </InputWithLabel>
        </Row>
        <Row>
          <InputWithLabel label="PDF url">
            <Controller
              control={control}
              name={"pdfUrl"}
              render={({ field }) => (
                <TextInput errorMessage={errors.pdfUrl?.message} {...field} />
              )}
            />
          </InputWithLabel>
        </Row>
        <Row>
          <InputWithLabel label="User group">
            <Controller
              control={control}
              name={"userGroupId"}
              render={({ field }) => (
                <Select<string>
                  disabled={disabledMap?.userGroupId}
                  errorMessage={errors.userGroupId?.message}
                  onChange={field.onChange}
                  value={field.value}
                  options={userGroupOptions}
                />
              )}
            />
          </InputWithLabel>
        </Row>
        <Row>
          <div className={"flex w-full justify-end"}>
            <PrimaryButton
              loading={loading}
              onClick={handleSubmit(onSubmit)}
              label={"Save"}
            />
          </div>
        </Row>
      </Col>
    </form>
  );
}
